import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export type UnwrapAngularForm<T> = T extends FormGroup<infer U> ? U : T extends FormControl<infer U> ? U : never;
export type TFormValue<T extends FormGroup> = ReturnType<T['getRawValue']>;

export function validate24hTimeInput(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    const hours = value.split(':')[0];
    const minutes = value.split(':')[1];

    // match 0 followed by 0-9 OR
    // match 1 followed by 0-9 OR
    // match 2 followed by 0-3
    const hoursValid = /^0[0-9]|1[0-9]|2[0-3]$/gm.test(hours);

    // match 0-5 followed by 0-9
    const minutesValid = /^[0-5][0-9]$/gm.test(minutes);

    return !hoursValid || !minutesValid ? { format: true } : null;
  };
}

export function check24hTimeRangeValidator<T extends FormGroup>(field1: keyof TFormValue<T> & string, field2: keyof TFormValue<T> & string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const start = control.get(field1);
    const end = control.get(field2);

    if (!start || !end || !start.value?.includes(':') || !end.value?.includes(':')) {
      return null;
    }

    const [startHours, startMinutes] = start.value.split(':').map((i: string) => +i);
    const [endHours, endMinutes] = end.value.split(':').map((i: string) => +i);

    if (endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)) {
      return { [`${field2}_before_${field1}`]: true };
    }

    return null;
  };
}

export function notBefore(date: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    return value < date ? { before: true } : null;
  };
}

export function notAfter(date: Date): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    return value > date ? { after: true } : null;
  };
}

export function zipValidator(requiredLength: number, isRequired: boolean = true): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    // Return no error if the field is empty.
    if (!value) {
      return isRequired ? { required: true, requiredLength } : null;
    }
    // Validate for numeric-only input.
    if (!/^\d+$/.test(value)) {
      return (!isRequired && requiredLength === 4) ? { nonNumericExtension: true } : { nonNumeric: true };
    }
    // Validate the required length.
    if (value.length < requiredLength) {
      return { minLength: { requiredLength, actualLength: value.length } };
    }
    return null;
  };
}

export function coupledFieldsValidator<T extends FormGroup>(field1: keyof TFormValue<T> & string, field2: keyof TFormValue<T> & string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const control1 = control.get(field1);
    const control2 = control.get(field2);

    if (!control1 || !control2) {
      return null;
    }

    let isControl1Empty = (control1.value as any) instanceof Array ? control1.value.length === 0 : !control1.value;
    let isControl2Empty = (control2.value as any) instanceof Array ? control2.value.length === 0 : !control2.value;

    // if control1 has a value, but control2 doesn't
    if (isControl1Empty && !isControl2Empty) {
      return { [`${field1}Empty`]: true };
    }

    // if control2 has a value, but control1 doesn't
    if (isControl2Empty && !isControl1Empty) {
      return { [`${field2}Empty`]: true };
    }

    return null;
  };
}

